import * as React from 'react';
import { Avatar, Box, Button, Card, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { getMyConversations } from '../../controllers/conversation/conversation';
import { getAccount } from '../../controllers/accounts/accounts';
import { getSinglePost } from '../../controllers/posts/posts';
import { getComments } from '../../controllers/posts/comments';
import { UserContext } from "../../context/UserContext";
import { Loading, stripMessageContent } from '../../util/util';

const NAV = '#202c41';
const MUTED = '#8A95A3';

const getTimeAgo = (dateString) => {
  const diffInMinutes = Math.floor((new Date() - new Date(dateString)) / (1000 * 60));
  if (diffInMinutes < 1) return 'now';
  if (diffInMinutes < 60) return `${diffInMinutes}m`;
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h`;
  if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)}d`;
  return new Date(dateString).toLocaleDateString();
};

export default function ConversationList() {
  const navigate = useNavigate();
  const { userDetails } = React.useContext(UserContext);
  const token = userDetails?.token ?? '';
  const myId = userDetails?.id;
  const { data, isLoading } = useQuery({
    queryKey: ['getMyConversations', myId, token],
    queryFn: () => getMyConversations(token, myId),
    enabled: !!token && !!myId,
  });

  const [conversations, setConversations] = React.useState([]);
  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const rows = data?.conversations ?? [];
      const resolved = await Promise.all(rows.map(async (row) => {
        const otherId = row.participants?.find(p => p !== myId);
        const [otherAccount, root] = await Promise.all([
          otherId ? getAccount(otherId, token).catch(() => null) : null,
          getSinglePost(row.status_id, token).catch(() => null),
        ]);
        // otherAccount/root are the minimum needed to show a row at all —
        // drop it only if those fail. The thread-context call below is just
        // for finding the true latest message; if it fails, fall back to the
        // root post alone rather than losing the whole conversation from the
        // list over what's likely a transient hiccup on that one call.
        if (!otherAccount?.id || !root?.id) return null;
        // The root status's own created_at never moves once someone
        // replies, so it can't stand in for "latest" — pull the whole
        // thread and take whichever post (root or a reply) is actually
        // newest to get a real last-activity time and preview.
        const context = await getComments(row.status_id, token).catch((err) => {
          console.error('ConversationList: failed to load thread context for', row.status_id, err);
          return null;
        });
        const thread = [root, ...(context?.descendants ?? [])];
        const lastStatus = thread.reduce((latest, post) =>
          new Date(post.created_at) > new Date(latest.created_at) ? post : latest, root);
        return { ...row, otherAccount, lastStatus };
      }));
      const sorted = resolved
        .filter(Boolean)
        .sort((a, b) => new Date(b.lastStatus.created_at) - new Date(a.lastStatus.created_at));
      if (!cancelled) setConversations(sorted);
    };
    if (data) load(); else setConversations([]);
    return () => { cancelled = true };
  }, [data, myId, token]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" fontWeight={800} sx={{
          background: 'linear-gradient(90deg,#E40303,#732982)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}>
          Messages
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/messages/create')}
          sx={{ borderRadius: 50, textTransform: 'none', fontWeight: 700, background: '#732982', '&:hover': { background: '#5c1f68' } }}
        >
          New Message
        </Button>
      </Box>

      {isLoading ? (
        <Loading />
      ) : conversations.length === 0 ? (
        <Box sx={{
          maxWidth: 400, mx: 'auto', mt: 8, textAlign: 'center',
          p: 4, borderRadius: 3,
          background: 'linear-gradient(120deg,#f3f0ff,#fff0f6)',
          border: '1px solid rgba(115,41,130,0.15)',
        }}>
          <Box sx={{ fontSize: 48, mb: 1.5 }}>💬</Box>
          <Typography fontWeight={800} fontSize={18} gutterBottom>No messages yet</Typography>
          <Typography variant="body2" color="text.secondary">
            Start a conversation by visiting someone's profile.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {conversations.map((item, i) => {
            const otherAccount = item.otherAccount;
            return (
              <Card
                key={item._id ?? i}
                onClick={() => navigate("/messages/" + item.status_id + "/" + item._id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 3,
                  cursor: 'pointer',
                  transition: 'transform 0.15s, box-shadow 0.15s',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <Avatar
                  alt={otherAccount.username}
                  src={otherAccount.avatar}
                  sx={{ width: 52, height: 52, border: '2px solid #ececf0' }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: item.unread ? 800 : 700, fontSize: 14, color: NAV }} noWrap>
                    {stripMessageContent(item.lastStatus.content)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Box sx={{
                      background: 'linear-gradient(90deg,#E40303,#732982)',
                      color: '#fff',
                      borderRadius: 50,
                      fontWeight: 700,
                      fontSize: 11,
                      px: 1.25,
                      py: 0.25,
                    }}>
                      @{otherAccount.acct}
                    </Box>
                    <Typography sx={{ fontSize: 12, color: MUTED }}>
                      {getTimeAgo(item.lastStatus.created_at)}
                    </Typography>
                  </Box>
                </Box>
                {item.unread && (
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: '#732982', flexShrink: 0 }} />
                )}
              </Card>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
